// pages/Traders/TradersList.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { 
  Trash2, Edit, Plus, Eye, Loader2, 
  Download, Filter, Search, RefreshCw,
  ChevronLeft, ChevronRight, FileText,
  Users, CheckCircle, XCircle, AlertCircle
} from "lucide-react";

function TradersList() {
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    rejected: 0
  });

  // ────────────────────────────────────────────────
  // API Instance Setup
  // ────────────────────────────────────────────────
  const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    headers: { 'Accept': 'application/json' }
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // ────────────────────────────────────────────────
  // Fetch Data from Laravel
  // ────────────────────────────────────────────────
  const FetchTrader = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/viewtrader');
      setLoading(false)
      if (response.data && response.data.data) {
        const tradersData = response.data.data;
        setTraders(tradersData);
        
        // Calculate stats
        setStats({
          total: tradersData.length,
          active: tradersData.filter(t => t.status === "approved" || t.status === "Active").length,
          pending: tradersData.filter(t => t.status === "pending").length,
          rejected: tradersData.filter(t => t.status === "rejected").length
        });
      } else {
        setTraders([]);
      }

    } catch (error) {
      console.error("Fetch error:", error);
      setTraders([]);
      
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Fetch Failed',
        text: 'Could not load traders from the server.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    FetchTrader();
  }, []);

  // ────────────────────────────────────────────────
  // Delete Logic
  // ────────────────────────────────────────────────
  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete Trader?',
      html: `<p class="text-gray-700">Are you sure you want to delete <span class="font-bold">${name}</span>?</p>
             <p class="text-sm text-red-600 mt-2">This action cannot be undone!</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'px-6 py-2.5 rounded-lg text-white font-medium',
        cancelButton: 'px-6 py-2.5 rounded-lg text-gray-700 font-medium'
      }
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/traders/${id}`);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Trader has been removed successfully.',
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
        FetchTrader();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete trader.',
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      }
    }
  };

  // ────────────────────────────────────────────────
  // Filtering & Pagination Logic
  // ────────────────────────────────────────────────
  const filteredTraders = traders.filter(trader => {
    // Search filter
    const matchesSearch = 
      trader.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trader.tin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trader.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trader.phone?.includes(searchTerm);
    
    // Status filter
    const matchesStatus = 
      statusFilter === "all" ? true :
      statusFilter === "active" ? (trader.status === "approved" || trader.status === "Active") :
      trader.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTraders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTraders.length / itemsPerPage);

  // ────────────────────────────────────────────────
  // Export CSV
  // ────────────────────────────────────────────────
  const exportToCSV = () => {
    const headers = ['TIN', 'Name', 'Business Name', 'Phone', 'Email', 'City', 'Status'];
    const csvData = filteredTraders.map(t => [
      t.tin,
      t.name,
      t.business_name || 'N/A',
      t.phone,
      t.email,
      t.city,
      t.status || 'pending'
    ]);
    
    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traders_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      approved: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
      active: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
      pending: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold 
                      rounded-full border ${config.color}`}>
        <Icon size={12} />
        {status || 'Pending'}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
            Traders
          </h1>
          <p className="text-gray-500 mt-1">
            Manage and monitor all registered traders
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 
                     rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 
                     transition-all shadow-sm"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Export</span>
          </button>
          
          <Link
            to="/admin/addtrader"
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r 
                     from-cyan-600 to-blue-600 text-white rounded-xl 
                     hover:from-cyan-700 hover:to-blue-700 transition-all 
                     shadow-lg shadow-cyan-600/20"
          >
            <Plus size={18} />
            <span>Add Trader</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">Total Traders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Users size={20} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.active}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">Rejected</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.rejected}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <XCircle size={20} className="text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          
          {/* Search */}
          <div className="relative w-full sm:w-96">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by TIN, name, business..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl
                       focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                       placeholder-gray-400"
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-300 
                         rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 
                         focus:border-cyan-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
              <Filter size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>

            {/* Refresh Button */}
            <button
              onClick={FetchTrader}
              className="p-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 
                       transition-colors"
              title="Refresh Data"
            >
              <RefreshCw size={18} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>



      

      {/* Table Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        
        {loading ? (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          {/* Spinner */}
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          
          {/* Inner icon */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-6 h-6 text-indigo-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        
        <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Your Data</h3>
        <p className="mt-2 text-gray-500">Please wait while we fetch your data...</p>
        
        {/* Loading steps */}
        <div className="mt-6 space-y-2">
          {['Fetching inspections', 'Loading statistics', 'Preparing charts'].map((step, index) => (
            <div key={step} className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-indigo-600 animate-pulse' : 'bg-gray-300'}`}></div>
              <span className={index === 0 ? 'text-indigo-600' : ''}>{step}</span>
              {index === 0 && (
                <svg className="w-3 h-3 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div> ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      TIN
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      Business Name
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                      Phone
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentItems.length > 0 ? (
                    currentItems.map((trader) => (
                      <tr 
                        key={trader.id} 
                        className="hover:bg-cyan-50/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {trader.tin}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{trader.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{trader.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          <p className="text-gray-700">{trader.business_name || "—"}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{trader.city}</p>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <span className="font-mono text-sm text-gray-700">
                            {trader.phone}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={trader.status} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/traders/${trader.id}`}
                              className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-lg 
                                     transition-colors"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </Link>
                           
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <Users size={48} className="text-gray-300 mb-3" />
                          <p className="text-gray-500 font-medium text-lg">
                            No traders found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm || statusFilter !== 'all' 
                              ? 'Try adjusting your filters'
                              : 'Get started by adding your first trader'}
                          </p>
                         
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredTraders.length > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredTraders.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredTraders.length}</span> traders
                </p>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 
                             disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={18} className="text-gray-600" />
                  </button>
                  
                  <span className="px-4 py-2 bg-cyan-50 text-cyan-700 rounded-lg 
                                 font-medium text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 
                             disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight size={18} className="text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TradersList;